<template>
    <section class="MigratedComplianceSection">
        <button
            type="button"
            class="MigratedComplianceSection__header"
            :aria-expanded="expanded ? 'true' : 'false'"
            :aria-controls="bodyId"
            @click="toggle"
        >
            <EidIcon
                class="MigratedComplianceSection__chevron"
                :icon-src="expanded ? faAngleDown : faAngleRight"
            />
            <span class="MigratedComplianceSection__headerText">
                <span class="MigratedComplianceSection__titleRow">
                    <span class="MigratedComplianceSection__title">Migrate existing compliance</span>
                    <span class="MigratedComplianceSection__optional">Optional</span>
                </span>
                <span class="MigratedComplianceSection__subtitle">
                    Only for requirements you’ve already verified outside Evident and don’t need the entity to submit now.
                </span>
            </span>
            <LabelBadge
                v-if="count > 0"
                class="MigratedComplianceSection__badge"
                variant="fill"
                color="warning"
                size="xs"
            >
                {{ count }} migrated
            </LabelBadge>
        </button>

        <transition name="MigratedComplianceSection__fade">
            <div
                v-show="expanded"
                :id="bodyId"
                class="MigratedComplianceSection__body"
            >
                <div class="MigratedComplianceSection__callout">
                    <strong class="MigratedComplianceSection__calloutTitle">What happens when you enter a date</strong>
                    <span>
                        The requirement is marked <strong>compliant with a migrated exception</strong> until that date.
                        Evident <strong>won’t ask the entity to submit</strong> a document for it until that date approaches.
                    </span>
                    <span>
                        Leave blank to request all documents from the entity right away.
                        You can remove migrated exceptions later from the entity’s profile.
                    </span>
                </div>
                <slot />
            </div>
        </transition>
    </section>
</template>

<style lang="scss">
    .MigratedComplianceSection {
        background: var(--eidWhite);
        border: 1px solid var(--eidTechGray-25);
        border-radius: 8px;

        &__header {
            align-items: flex-start;
            background: transparent;
            border: 0;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            gap: 12px;
            padding: 14px 16px;
            text-align: left;
            width: 100%;

            &:hover {
                background: var(--eidTechGray-10);
            }

            &:focus-visible {
                box-shadow: 0 0 0 2px var(--eidWhite), 0 0 0 4px var(--eidTrustGreen);
                outline: none;
            }
        }

        &__chevron {
            color: var(--eidTechGray);
            flex: none;
            font-size: 13px;
            padding-top: 2px;
            width: 12px;
        }

        &__headerText {
            display: flex;
            flex: 1;
            flex-direction: column;
            gap: 3px;
        }

        &__titleRow {
            align-items: center;
            display: flex;
            gap: 8px;
        }

        &__title {
            color: var(--eidSecureBlue);
            font-size: var(--text-body-size, 14px);
            font-weight: 600;
        }

        &__optional {
            color: var(--eidTechGray);
            font-size: 12px;
        }

        &__subtitle {
            color: var(--eidQualityBlack-75);
            font-size: var(--text-small-size, 13px);
            line-height: 1.45;
        }

        &__badge {
            flex: none;
        }

        &__body {
            display: flex;
            flex-direction: column;
            gap: 14px;
            padding: 0 16px 16px 40px;
        }

        &__callout {
            background: var(--eidSafetyBlue-10);
            border: 1px solid var(--eidSafetyBlue-25);
            border-radius: 8px;
            color: var(--eidQualityBlack);
            display: flex;
            flex-direction: column;
            font-size: var(--text-small-size, 13px);
            gap: 6px;
            line-height: 1.5;
            padding: 12px 14px;
        }

        &__calloutTitle {
            color: var(--eidSecureBlue);
            font-weight: 600;
        }

        &__fade-enter-active,
        &__fade-leave-active {
            transition: opacity 0.15s ease;
        }

        &__fade-enter-from,
        &__fade-leave-to {
            opacity: 0;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleDown, faAngleRight } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { LabelBadge } from '@evidentid/dashboard-commons/components/LabelBadge';

    let nextBodyId = 0;

    export default defineComponent({
        name: 'MigratedComplianceSection',
        components: { EidIcon, LabelBadge },
        props: {
            count: {
                type: Number as PropType<number>,
                default: 0,
            },
            // Opens a collapsed section, and keeps it open, so a validation error inside it is never
            // hidden: a hidden error leaves Save refusing with no reason on screen.
            hasError: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        data() {
            nextBodyId += 1;

            return {
                expanded: false,
                bodyId: `MigratedComplianceSection-body-${nextBodyId}`,
                faAngleDown,
                faAngleRight,
            };
        },
        watch: {
            hasError: {
                immediate: true,
                handler(value: boolean): void {
                    if (value) {
                        this.expanded = true;
                    }
                },
            },
        },
        methods: {
            toggle(): void {
                if (this.expanded && this.hasError) {
                    return;
                }

                this.expanded = !this.expanded;
            },
        },
    });
</script>
